import { create } from "zustand";
import type { SystemAdmin } from "@/services/auth";
import {
	getStoredAdminData,
	isAuthenticated,
	clearAuthData,
	login as authLogin,
	logout as authLogout,
	getMe,
	verifyAuth,
} from "@/services/auth";

interface AuthState {
	// Auth state
	admin: SystemAdmin | null;
	isAuthenticated: boolean;
	isLoading: boolean;
	error: string | null;

	// Actions
	initialize: () => Promise<void>;
	login: (email: string, password: string) => Promise<boolean>;
	logout: () => Promise<void>;
	refreshProfile: () => Promise<void>;
	clearError: () => void;
	setError: (error: string) => void;
}

export const useAuthStore = create<AuthState>((set, get) => ({
	// Initial state
	admin: null,
	isAuthenticated: false,
	isLoading: true,
	error: null,

	/**
	 * Initialize auth state from localStorage
	 * Call this on app mount to restore session
	 */
	initialize: async () => {
		set({ isLoading: true, error: null });

		try {
			// Check if we have stored auth data
			if (!isAuthenticated()) {
				set({ admin: null, isAuthenticated: false, isLoading: false });
				return;
			}

			// Try to get stored admin data first
			const storedAdmin = getStoredAdminData();
			if (storedAdmin) {
				set({ admin: storedAdmin, isAuthenticated: true });
			}

			// Verify token is still valid by calling API
			const isValid = await verifyAuth();
			if (!isValid) {
				// Token is invalid, clear auth data
				clearAuthData();
				set({ admin: null, isAuthenticated: false, isLoading: false });
				return;
			}

			// Get fresh admin data
			const admin = await getMe();
			set({ admin, isAuthenticated: true, isLoading: false });
		} catch (error) {
			console.error("Failed to initialize auth:", error);
			clearAuthData();
			set({ admin: null, isAuthenticated: false, isLoading: false });
		}
	},

	/**
	 * Login with email and password
	 */
	login: async (email: string, password: string) => {
		set({ isLoading: true, error: null });

		try {
			const result = await authLogin(email, password);
			set({
				admin: result.admin,
				isAuthenticated: true,
				isLoading: false,
				error: null,
			});
			return true;
		} catch (error: any) {
			const message = error?.message || "Login failed. Please try again.";
			set({ isLoading: false, error: message });
			return false;
		}
	},

	/**
	 * Logout current admin
	 */
	logout: async () => {
		set({ isLoading: true });

		try {
			await authLogout();
		} catch (error) {
			console.error("Logout error:", error);
		} finally {
			clearAuthData();
			set({
				admin: null,
				isAuthenticated: false,
				isLoading: false,
				error: null,
			});
		}
	},

	/**
	 * Refresh admin profile from server
	 */
	refreshProfile: async () => {
		if (!get().isAuthenticated) return;

		try {
			const admin = await getMe();
			set({ admin });
		} catch (error) {
			console.error("Failed to refresh profile:", error);
		}
	},

	/**
	 * Clear current error
	 */
	clearError: () => {
		set({ error: null });
	},

	/**
	 * Set error message
	 */
	setError: (error: string) => {
		set({ error });
	},
}));
